import { TFunction } from 'i18next'

import { PropertyManager } from '@/api/property-managers'
import { SelectOption } from '@/components/ui/select'
import { SortOrder } from '@/components/ui/table'
import { formatDateOnly } from '@/utils/date'
import { formatPhoneNumberDisplay } from '@/utils/phone-number'

import { filterBySearch, getPersonFullName, sortItems } from '../shared/person-table.utils'
import { PropertyManagerSortKey } from './property-managers.sort-keys'

export const getPropertyManagerFullName = (propertyManager: PropertyManager) =>
  getPersonFullName(propertyManager.user)

export const filterPropertyManagersByBuildings = (
  propertyManagers: PropertyManager[],
  buildingIds?: number[],
) => {
  if (!buildingIds?.length) return propertyManagers

  return propertyManagers.filter((propertyManager) =>
    propertyManager.buildingIds.some((building) => buildingIds.includes(building.id)),
  )
}

export const getPropertyManagerBuildingOptions = (
  propertyManagers: PropertyManager[],
): SelectOption<number>[] => {
  const buildingsById = new Map<number, string>()
  propertyManagers.forEach((propertyManager) =>
    propertyManager.buildingIds.forEach((building) =>
      buildingsById.set(building.id, building.name),
    ),
  )

  return [...buildingsById]
    .map(([value, label]) => ({ label, value }))
    .sort((a, b) => a.label.localeCompare(b.label))
}

export const filterPropertyManagers = (propertyManagers: PropertyManager[], search?: string) =>
  filterBySearch(
    propertyManagers,
    search,
    (propertyManager) => [
      getPropertyManagerFullName(propertyManager),
      propertyManager.user.email,
      propertyManager.jobTitle,
      ...propertyManager.buildingIds.map((building) => building.name),
    ],
    (propertyManager) => propertyManager.phone,
  )

const getSortValue = (propertyManager: PropertyManager, sortBy: PropertyManagerSortKey) => {
  switch (sortBy) {
    case PropertyManagerSortKey.buildings:
      return propertyManager.buildingIds.length
    case PropertyManagerSortKey.createdAt:
      return propertyManager.createdAt
    case PropertyManagerSortKey.email:
      return propertyManager.user.email.toLowerCase()
    case PropertyManagerSortKey.name:
      return getPropertyManagerFullName(propertyManager).toLowerCase()
    case PropertyManagerSortKey.phone:
      return propertyManager.phone ?? 0
  }
}

export const sortPropertyManagers = (
  propertyManagers: PropertyManager[],
  sortBy?: PropertyManagerSortKey | null,
  sortOrder?: SortOrder | null,
) => sortItems(propertyManagers, sortBy, sortOrder, getSortValue)

export const getPropertyManagersCsvFields = (t: TFunction<['organisation']>) => [
  {
    label: t('organisation:propertyManagers.list.columns.name'),
    value: (row: PropertyManager) => getPropertyManagerFullName(row),
  },
  {
    label: t('organisation:propertyManagers.list.columns.email'),
    value: (row: PropertyManager) => row.user.email,
  },
  {
    label: t('organisation:propertyManagers.list.columns.phone'),
    value: (row: PropertyManager) => formatPhoneNumberDisplay(row.phone),
  },
  {
    label: t('organisation:propertyManagers.detailsModal.jobTitle'),
    value: (row: PropertyManager) => row.jobTitle ?? '',
  },
  {
    label: t('organisation:propertyManagers.list.columns.buildings'),
    value: (row: PropertyManager) => row.buildingIds.map((building) => building.name).join('; '),
  },
  {
    label: t('organisation:propertyManagers.detailsModal.status'),
    value: (row: PropertyManager) =>
      row.user.isActive
        ? t('organisation:propertyManagers.detailsModal.active')
        : t('organisation:propertyManagers.detailsModal.inactive'),
  },
  {
    label: t('organisation:propertyManagers.list.columns.createdAt'),
    value: (row: PropertyManager) => formatDateOnly(new Date(row.createdAt)),
  },
]
